/* eslint-disable react/react-in-jsx-scope */
import { useIsFocused } from '@react-navigation/native';
import appManager from 'AppManager';
import { Device, WEB_WIDTH } from 'DeviceUtils';
import { ActivityIndicator, Packshot, Footer } from 'LocalGluons';
import NavigationService from 'NavigationService';
import { TV_PLACEHOLDER, VOD_PLACEHOLDER } from 'Options';
import { PAGE_BY, PageItem } from 'Pages/Page/PageItem';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View } from 'react-native';
import { SectionGrid } from 'react-native-super-grid';
import Theme from 'Theme';
import {
    getAssetImageUrl,
    getAssetTitle,
    getImageKeyByItemType,
    getItemSubtitle,
    getPackshotDimensionsByModelType,
    isArchive,
    isLiveTv,
    ITEM_TYPES
} from 'Utils';
import { SearchResultModel } from '@24i/st-api-connector/interface/search/models/SearchResultModel';
import { handleScroll, show } from 'ReduxStore/webMenu';
import { useAppDispatch, useAppSelector } from 'ReduxStore/hooks';
import { useNavigationPropsContext } from '../../Navigation/navigationPropsContext';
import ContentWrapper from '../ContentWrapper';
import styles from './ViewAll.jss';
import { TOP_BAR_ITEMS_HEIGHT, TOP_BAR_TOTAL_HEIGHT, DETAIL_HEADER_HEIGHT } from '../../Navigation/Menu/Menu.styles';

const getProgress = (item) =>
    typeof item.getProgressPercent === 'function' &&
        item.modelType !== ITEM_TYPES.VOD &&
        !isArchive(item)
        ? item.getProgressPercent(new Date())
        : 0;

const ViewAll = ({ navigation, route, isFocused }) => {
    const [localItems, setItems] = useState([]);
    const [isEndReached, setIsEndReached] = useState(false);
    const [loading, setLoading] = useState(true);
    const [itemDimension, setItemDimension] = useState(null);
    const [loadItemsBlocked, setLoadItemsBlocked] = useState(false);
    const scrollOffset = useRef(0);
    const offset = useRef(0);
    const flatGridRef = useRef(null);
    const url = useRef(route.params?.playlistUrl ?? null);
    const { navigate } = useNavigationPropsContext();
    const dispatch = useAppDispatch();
    const isMinimised = useAppSelector((state) => state.webMenu.isMinimised);

    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (url.current) {
            loadNewItems();
        } else {
            setItems(route.params?.items || []);
            setIsEndReached(true);
            setLoading(false);
            setLoadItemsBlocked(false);
        }
        dispatch(show());
        NavigationService.setAppNavigationInstance(navigation);
    }, []);

    // useEffect(() => {
    //     if (isFocused) flatGridRef.current?.scrollToOffset({ offset: scrollOffset.current, animated: false });
    // }, [isFocused]);

    const onItemPress = (item) => {
        const { originalModelType, modelType, data } = item;
        const type = originalModelType || modelType;

        const id = data?.type === ITEM_TYPES.PVR
            ? data.scheduleId
            : type === ITEM_TYPES.RECORDING
                ? data.programId
                : item instanceof SearchResultModel ? item.getId() : data.id;
        const platform = data?.pckg?.platform;

        navigate('Details', { item }, { id, type, platform });
    };

    const onEndReached = async () => {
        if (!isEndReached && !loadItemsBlocked) {
            setLoadItemsBlocked(true);
            await loadNewItems();
        }
    };

    // const onScroll = (event) => {

    //     if (isFocused) scrollOffset.current = event.nativeEvent.contentOffset.y;
    // };

    const scrollTriggerLock = useRef(false);
    const lastScrollY = useRef(0);

    const loadNewItems = async () => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (!isFocused || !url.current || isEndReached) return;
        const newOffset = offset.current + (localItems.length ? PAGE_BY : 0);
        let offsetUrl = url.current;
        if (url.current?.includes('limit=')) {
            offsetUrl = url.current?.includes('offset=')
                ? url.current?.replace(/offset=(\d+)/, `offset=${newOffset}`)
                : `${url.current}&offset=${newOffset}`;
        }
        try {
            const { items, total } = await appManager.getPlaylistItems({ playlistId: offsetUrl });
            const dataType = PageItem.getCarouselDataType(items, offsetUrl);
            // needed to change items property via ContinueWatchingItem
            const extendedItems = PageItem.parseItemsByDataType(items, dataType);
            const endWasReached = !url.current?.includes('limit=') ||
                items.length === 0 ||
                items.length + localItems.length >= total;
            offset.current = newOffset;
            setItems((oldItems) => [...oldItems, ...items]);
            setIsEndReached(endWasReached);
            setLoading(false);
            setLoadItemsBlocked(false);
        } catch (error) {
            console.log(error);
        }
    };

    const renderItem = useCallback(({ item }) => {
        const showMetadata = route.params?.showMetadata || false;
        const dimensions = getPackshotDimensionsByModelType(item.modelType);
        setItemDimension(dimensions.width);
        const parentalStandard = item.data?.parentalRating;
        const parentalRecording =
            item.metaData?.detail?.schedule?.program?.programValue?.parentalRating;
        const parentalRecordingSeries =
            item.metadata?.metaData?.detail?.schedule?.program?.programValue?.parentalRating;

        const adultStandard = item.data?.adult;
        const adultRecording = item.metaData?.detail?.schedule?.program?.programValue?.ao;
        const adultRecordingSeries =
            item.metadata?.metaData?.detail?.schedule?.program?.programValue?.ao;

        return (
            <Packshot
                item={item}
                key={item.getId() + item.data.category}
                title={getAssetTitle(item)}
                subTitle={getItemSubtitle(item)}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                color={Theme.primaryTextColor}
                activeColor={Theme.primaryActiveColor}
                imageUri={getAssetImageUrl(item, getImageKeyByItemType(item), 'LOW')}
                onPress={() => {
                    onItemPress(item);
                }}
                progress={getProgress(item)}
                iconUri={item.data?.channelLogoUrl || ''}
                imageDimensions={{
                    width: '100%',
                    height: dimensions.height
                }}
                placeholderImage={isLiveTv(item) ? TV_PLACEHOLDER : VOD_PLACEHOLDER}
                parentalRating={
                    parentalStandard || parentalRecording || parentalRecordingSeries
                }
                adult={adultStandard || adultRecording || adultRecordingSeries}
                packageImageUrl={item.data?.pckg?.imageUrl}
                showMetadata={showMetadata}
            />
        );
    }, []);

    /*     const ListFooterComponent = () => {
            if (loadItemsBlocked) {
                return <ActivityIndicator style={styles.footerIndicator} size={Device.isTablet ? 'large' : 'small'} color={Theme.primaryActiveColor} />;
            }
        }; */

    const FooterComponent = useMemo(() => <View style={{ width: '100%' }}>{loadItemsBlocked && <ActivityIndicator style={styles.footerIndicator} size={Device.isTablet ? 'large' : 'small'} color={Theme.primaryActiveColor} />}<Footer /></View>, [loadItemsBlocked]);
    const sectionGrid = (
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        <SectionGrid
            onScroll={(e) => dispatch(handleScroll(e))}
            sections={[{ data: localItems }]}
            style={{ ...styles.gridView }}
            itemDimension={itemDimension}
            renderItem={renderItem}
            onEndReached={onEndReached}
            onEndReachedThreshold={5}
            ref={flatGridRef}
            ListFooterComponent={FooterComponent}
            ListFooterComponentStyle={{ width: '100%' }}
            contentContainerStyle={{ alignItems: 'center' }}
            maxDimension={Device.isWeb ? WEB_WIDTH : undefined}
        />
    );

    const content = (
        <ContentWrapper noScrollView loading={loading}>
            {sectionGrid}
        </ContentWrapper>
    );

    return Device.isWeb ? (
        <View style={{ marginTop: !isMinimised ? TOP_BAR_TOTAL_HEIGHT + DETAIL_HEADER_HEIGHT : TOP_BAR_ITEMS_HEIGHT, flex: 1 }}>
            {content}
        </View>
    ) : (
        content
    );
};

export default function (props) {
    const isFocused = useIsFocused();

    return <ViewAll {...props} isFocused={isFocused} />;
}
